import {useState, useEffect} from 'react';
import {Button} from '~/components/ui/button';
import {Input} from '~/components/ui/input';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '~/components/ui/card';
import type {Organization, OrganizationEvent} from '~/types';
import {Link, useFetcher} from 'react-router';
import {authClient} from '~/services/auth-client';
import {validateEmail} from '~/utils/validateEmail';
import {Alert, AlertDescription, AlertTitle} from '~/components/ui/alert';
import {AlertCircle, ChevronLeft, ChevronRight, MailCheck} from 'lucide-react';
import type {EmailSentActionData} from '~/pages/EmailSent/action';
import {TOO_MANY_ATTEMPTS} from '~/services/ResendEmailService/resendFailureReason';

interface EventLoginProps {
  event: OrganizationEvent;
  organization: Organization;
  hasCustomDomain: boolean;
  redirectPath?: string;
}

export function EventLogin({
  event,
  organization,
  hasCustomDomain,
  redirectPath,
}: EventLoginProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  const fetcher = useFetcher<EmailSentActionData>();
  const emailIsValid = validateEmail(email);

  // Update success state based on fetcher result
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data && !fetcher.data.failureReason) {
      setResendSuccess(true);
    } else if (fetcher.state === 'submitting') {
      setResendSuccess(false);
    }
  }, [fetcher.state, fetcher.data]);

  const handleSignIn = async () => {
    if (!emailIsValid) return;

    setIsLoading(true);

    const prefix = hasCustomDomain ? '' : `/${organization.slug}`;

    const callbackPath = redirectPath ?? '';
    const {error} = await authClient.signIn.magicLink({
      email,
      callbackURL: `${prefix}${callbackPath}`,
      errorCallbackURL: `${prefix}/login?error=true`,
    });

    // Magic link shouldn't error
    if (error) {
      throw error;
    } else {
      setEmailSentTo(email);
    }

    setIsLoading(false);
  };

  const handleResend = () => {
    if (!emailSentTo) return;

    const prefix = hasCustomDomain ? '' : `/${organization.slug}`;
    const actionUrl = `${prefix}/email-sent`;

    fetcher.submit(
      {email: emailSentTo},
      {method: 'post', action: actionUrl}
    );
  };

  const isResending = fetcher.state === 'submitting';
  const failureReason = fetcher.data?.failureReason;

  if (emailSentTo) {
    return (
      <div className="flex w-full items-center justify-center p-2">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-center text-3xl md:text-4xl font-bold">
              Check Your Email To Continue
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <MailCheck strokeWidth={1.5} className="mx-auto my-2 h-18 w-18 text-gray-600" />
              <div className="font-medium text-xl md:text-2xl border rounded-md p-2 my-2 bg-secondary text-secondary-foreground">{emailSentTo}</div>
              <Button
                className="mt-2"
                variant="link"
                onClick={() => {
                  setEmailSentTo(null);
                  setResendSuccess(false);
                }}
              >
                Use a different email <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>What&apos;s next?</AlertTitle>
              <AlertDescription>
                Click the link in the email to complete your sign-in and continue
                with {event.name} registration.
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex flex-col items-center">
            <p className="mb-2 text-sm text-gray-500">
              Didn&apos;t receive the email? Check your spam folder.
            </p>
            <Button
              onClick={handleResend}
              disabled={isResending || failureReason === TOO_MANY_ATTEMPTS}
            >
              {isResending ? 'Resending...' : 'Resend Sign-In Link'}
            </Button>
            {resendSuccess && (
              <p className="mt-2 text-sm text-green-500">
                Sign-in link resent successfully!
              </p>
            )}
            {failureReason === TOO_MANY_ATTEMPTS && (
              <p className="mt-2 text-sm text-red-500">
                You&apos;ve retried too many times.
              </p>
            )}
            {failureReason && failureReason !== TOO_MANY_ATTEMPTS && (
              <p className="mt-2 text-sm text-red-500">
                Failed to resend.
              </p>
            )}
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-center p-4">
      <Card className="min-h-[300px] w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-3xl md:text-5xl">Register for {event.name}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-base md:text-xl">
          Enter your email to register for {event.name} or manage your existing
          registration.
        </CardContent>
        <CardFooter>
          <div className="flex w-full flex-col">
            <div className="flex w-full flex-col md:flex-row gap-2">
              <Input
                id="email"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(ev) => {
                  setEmail(ev.target.value);
                }}
              />
              {emailIsValid ? (
                <Button onClick={handleSignIn} disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Start registration'}
                </Button>
              ) : (
                <Button disabled>Start registration</Button>
              )}
            </div>
            <div className="flex flex-col md:flex-row justify-end items-end text-xs text-muted-foreground mt-2 md:mt-4">
              By proceeding, you accept the&nbsp;
              <div className='flex flex-row md:flex-row'>
                <Link to="/terms-of-service" className="underline">
                  Terms of Service
                </Link>
                &nbsp;and&nbsp;
                <Link to="/privacy-policy" className="underline">
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
